import React, { useState, useEffect } from 'react';
import { Briefcase, Filter, Search, TrendingUp, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ApplicationStatusCard, ApplicationTimeline } from '../components';
import {
  getUserApplications,
  getApplicationStats,
  syncApplicationsFromTests
} from '../services/applicationService';

const Applications = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadApplications();
  }, [user]);

  const loadApplications = async () => {
    if (!user?.id) return;

    setLoading(true);
    const [apps, appStats] = await Promise.all([
      getUserApplications(user.id),
      getApplicationStats(user.id)
    ]);

    setApplications(apps);
    setStats(appStats);
    setLoading(false);
  };

  const handleSync = async () => {
    if (!user?.id) return;

    setSyncing(true);
    const synced = await syncApplicationsFromTests(user.id);
    if (synced > 0) {
      await loadApplications();
    }
    setSyncing(false);
  };

  // Filter applications
  const filteredApps = applications.filter(app => {
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchesSearch = app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (app.companyName && app.companyName.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const statusFilters = [
    { value: 'all', label: 'All', count: applications.length },
    { value: 'applied', label: 'Applied', count: stats?.applied || 0 },
    { value: 'test_completed', label: 'Test Done', count: stats?.testCompleted || 0 },
    { value: 'under_review', label: 'In Review', count: stats?.underReview || 0 },
    { value: 'shortlisted', label: 'Shortlisted', count: stats?.shortlisted || 0 },
    { value: 'interview_scheduled', label: 'Interview', count: stats?.interviewScheduled || 0 },
    { value: 'rejected', label: 'Rejected', count: stats?.rejected || 0 }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        <span className="ml-4 text-lg text-gray-600">Loading applications...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-800 to-green-600 text-white rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center space-x-3">
              <Briefcase className="w-8 h-8" />
              <span>My Applications</span>
            </h1>
            <p className="text-green-100 mt-2">Track your job applications and test submissions</p>
          </div>

          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <TrendingUp className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            <span>Sync Tests</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-600">Total Applications</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-200 shadow-sm">
            <p className="text-sm text-green-700">Success Rate</p>
            <p className="text-3xl font-bold text-green-600 mt-1">{stats.successRate}%</p>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 shadow-sm">
            <p className="text-sm text-blue-700">Avg Response</p>
            <p className="text-3xl font-bold text-blue-600 mt-1">{stats.avgResponseTime}d</p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 shadow-sm">
            <p className="text-sm text-yellow-700">In Progress</p>
            <p className="text-3xl font-bold text-yellow-600 mt-1">
              {stats.testCompleted + stats.underReview}
            </p>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Status Filters */}
          <div className="flex items-center space-x-2 overflow-x-auto pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
            {statusFilters.map(filter => (
              <button
                key={filter.value}
                onClick={() => setFilterStatus(filter.value)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0
                  ${filterStatus === filter.value
                    ? 'bg-red-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {filter.label} ({filter.count})
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Applications Grid/Detail View */}
      {selectedApp ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <button
              onClick={() => setSelectedApp(null)}
              className="mb-4 text-red-600 hover:text-red-700 font-medium text-sm"
            >
              ← Back to all applications
            </button>

            <ApplicationStatusCard
              application={selectedApp}
              onClick={() => setSelectedApp(null)}
            />
          </div>

          <div className="lg:col-span-2">
            <ApplicationTimeline application={selectedApp} />
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredApps.length > 0 ? (
            filteredApps.map(app => (
              <ApplicationStatusCard
                key={app.id}
                application={app}
                onClick={() => setSelectedApp(app)}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No applications found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Start applying to jobs and track your progress here'
                }
              </p>
              {applications.length === 0 && (
                <button
                  onClick={handleSync}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Sync from Test Results
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Applications;
